import { createFileRoute, Link } from "@tanstack/react-router";
import { Layout } from "@/components/site/Layout";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Mail, ArrowRight } from "lucide-react";
import faqHeroBg from "@/assets/faq-hero-bg.webp";

import { FAQ_GROUPS as groups } from "@/content/faq";

const faqs = groups.flatMap((g) => g.items);
const TITLE = "Gold Trading FAQ: Signals, Risk & Membership | XAU Pro Academy";
const DESC =
  "Answers on the OTAS-01 engine, SMC methodology, setup grades, Telegram alerts, 1R risk, drawdown, prop firm compatibility, and membership.";

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://xauproacademy.com/faq" },
    ],
    links: [{ rel: "canonical", href: "https://xauproacademy.com/faq" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://xauproacademy.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "FAQ",
              item: "https://xauproacademy.com/faq",
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqs.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: FAQPage,
});

const related = [
  { to: "/track-record", label: "Performance Record" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/position-size-calculator", label: "Position Size Calculator" },
  { to: "/pricing", label: "Membership" },
] as const;

function FAQPage() {
  return (
    <Layout>
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <img
          src={faqHeroBg}
          alt="XAU Pro Academy frequently asked questions about Orion AI, OTAS-01 and OSAS-01"
          width={1920}
          height={1080}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-luminosity"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/55 to-background" />
        <div className="container relative mx-auto max-w-7xl px-4 pb-20 pt-20 md:pt-28">
          <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
            <span className="inline-flex items-center rounded-full border border-gold/30 bg-gold-muted px-3 py-1 font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
              Support
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold tracking-tight md:text-6xl">
              Frequently Asked <span className="text-gradient-gold">Questions</span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">
              Methodology, execution, risk, and membership. Answered plainly.
            </p>
          </div>
          <nav aria-label="FAQ sections" className="mx-auto mt-10 flex max-w-3xl flex-wrap justify-center gap-2">
            {groups.map((g) => (
              <a
                key={g.id}
                href={`#${g.id}`}
                className="rounded-full border border-border/60 bg-card/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:border-gold/40 hover:text-gold"
              >
                {g.title}
              </a>
            ))}
          </nav>
        </div>
      </section>

      <section className="container mx-auto max-w-3xl space-y-12 px-4 py-14">
        {groups.map((g) => (
          <div key={g.id} id={g.id} className="scroll-mt-24">
            <h2 className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold">{g.title}</h2>
            <Accordion type="single" collapsible className="mt-4 space-y-3">
              {g.items.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`${g.id}-${i}`}
                  className="overflow-hidden rounded-xl border border-border/60 bg-card px-5 data-[state=open]:border-gold/40"
                >
                  <AccordionTrigger className="py-5 text-left font-display text-base font-semibold hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        ))}

        <div className="rounded-xl border border-border/60 bg-card p-6">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.24em] text-muted-foreground">Related</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {related.map((r) => (
              <Link key={r.to} to={r.to} className="flex items-center justify-between rounded-md border border-border/60 px-4 py-3 text-sm hover:border-gold/40 hover:text-gold">
                {r.label} <ArrowRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-gold/30 bg-gold-muted/40 p-8 text-center">
          <Mail className="mx-auto h-8 w-8 text-gold" />
          <h2 className="mt-3 font-display text-xl font-bold">Still have questions?</h2>
          <p className="mt-2 text-sm text-muted-foreground">Email support@xauproacademy.com, Monday to Friday.</p>
          <div className="mt-5 flex flex-col items-center justify-center gap-2 sm:flex-row">
            <Button asChild variant="gold">
              <Link to="/auth" search={{ tab: "signup" }}>
                Create Free Account <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <a href="mailto:support@xauproacademy.com">
                <Mail className="h-4 w-4" /> Email Support
              </a>
            </Button>
          </div>
        </div>
      </section>
    </Layout>
  );
}
